'use client';
import { cn } from '@coas/ui/lib';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { memo, useMemo } from 'react';
import type { RouterOutputs } from 'trpc/shared';
import { PriceWithDiscount } from '~/entities/DisplayedPrice';
import { RemoveFromWishlistButton } from '~/entities/Wishlist';
import { imgLoader } from '~/shared/lib';

type WishlistCardProps = {
    wishlistItem: RouterOutputs['wishlist']['getAll']['items'][number];
};

export default memo(({ wishlistItem }: WishlistCardProps) => {
    const productImage = useMemo(() => wishlistItem.product.image ?? null, [wishlistItem.product.image]);

    const { isAvailable, isConst } = useMemo(() => {
        return {
            isAvailable: wishlistItem.displayedPrice.price !== null,
            isConst: wishlistItem.displayedPrice.strategy === 'const',
        };
    }, [wishlistItem.displayedPrice]);

    const t = useTranslations();

    return (
        <div className='flex flex-row gap-x-4'>
            <div className='relative h-36 w-28 bg-white'>
                {!!productImage?.key && (
                    <Link href={`/products/${wishlistItem.product.slug}`} className='relative block h-full w-full'>
                        <Image
                            src={productImage.key}
                            loader={imgLoader({
                                src: productImage.key,
                                width: productImage.width,
                                height: productImage.height,
                            })}
                            placeholder='blur'
                            blurDataURL={productImage.lqip ?? undefined}
                            className={cn(
                                productImage.width && productImage.height && productImage.width > productImage.height
                                    ? 'object-contain'
                                    : 'object-cover',
                            )}
                            alt={productImage.alt ?? ''}
                            fill
                        />
                    </Link>
                )}
            </div>

            <div className='flex min-w-0 flex-1 flex-col gap-y-1'>
                <div className='grid grid-cols-[1fr_auto] items-start gap-x-2'>
                    <span className='truncate text-14 font-500 uppercase text-neutral-500 col-start-1'>
                        {wishlistItem.product.categories.map(({ category }) => category.name).join(', ')}
                    </span>

                    <RemoveFromWishlistButton
                        id={wishlistItem.product.id}
                        className='size-5 p-0.5 flex-none shrink-0 col-start-2 row-span-2 self-start'
                    />

                    <Link
                        href={`/products/${wishlistItem.product.slug}`}
                        className='line-clamp-2 font-500 leading-[1.1] col-start-1 min-w-0'
                    >
                        {wishlistItem.product.name}
                    </Link>
                </div>

                <span className={'font-600 font-futura'}>
                    {isAvailable && isConst && <PriceWithDiscount displayPrice={wishlistItem.displayedPrice} />}

                    {isAvailable && !isConst && (
                        <>
                            {t('productCard.fromPrice')} <PriceWithDiscount displayPrice={wishlistItem.displayedPrice} />
                        </>
                    )}

                    {!isAvailable && t('productCard.outOfStock')}
                </span>
            </div>
        </div>
    );
});
